import React from 'react';

// Party chips — kids, dog, group of 6+, wheelchair, big rig. Each option carries the
// value the database expects (true, or the group size), so selecting one sets
// party[key] = option.value and clearing it removes the key.
//
// Item 4 — chips now carry a small icon and split into two labeled micro-groups
// (Party / Access), and an active chip is a filled pill with an × affordance;
// tapping an active chip removes it individually. Labels, keys and values still
// come straight from the party_options data — only the icon glyph and the group
// assignment are static (the "static icons and new group labels" the tracker
// exempts from data-driven rendering). lucide-react is not a project dependency,
// so each icon is an inline <svg> mirroring the lucide-react shape (the codebase
// convention; see AttachmentList.js / WorkFilters.js).

const Icon = ({ children }) => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ICONS = {
  // Kids — a baby face with a little curl on top.
  kids: (
    <Icon>
      <circle cx="12" cy="13" r="7" />
      <path d="M9 11h.01" />
      <path d="M15 11h.01" />
      <path d="M9.5 15c.6.6 1.5 1 2.5 1s1.9-.4 2.5-1" />
      <path d="M12 6a1.5 1.5 0 0 1 1.5 1.5" />
    </Icon>
  ),
  // Dog — a paw print (four toe pads + main pad).
  dog: (
    <Icon>
      <circle cx="11" cy="5" r="1.8" />
      <circle cx="6.5" cy="10" r="1.8" />
      <circle cx="17.5" cy="10" r="1.8" />
      <path d="M12 11c-3 0-5 1.8-5 4 0 1.8 1.6 3 3.5 3h3c1.9 0 3.5-1.2 3.5-3 0-2.2-2-4-5-4z" />
    </Icon>
  ),
  // Group of 6+ — two figures (lucide "users").
  group: (
    <Icon>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Icon>
  ),
  // Wheelchair — a seated figure with a wheel.
  accessible: (
    <Icon>
      <circle cx="11" cy="4" r="2" />
      <path d="M11 6.5v4.5" />
      <path d="M11 11h3.5" />
      <path d="M11 11l2 3.5" />
      <circle cx="15" cy="17" r="4.5" />
    </Icon>
  ),
  // Big rig — a truck (cargo box + cab + two wheels).
  big_rig: (
    <Icon>
      <path d="M2 6a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v9H2z" />
      <path d="M13 9h4l3 3v3h-7" />
      <path d="M2 15h12" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
    </Icon>
  ),
};

// The small × shown on an active chip — tapping the active chip removes it.
const XIcon = () => (
  <svg className="w-3 h-3 shrink-0 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

// Group-header icons — a representative glyph leading each micro-group label,
// matching the way the When control leads with a clock icon.
const HeaderIcon = ({ children }) => (
  <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const GROUP_ICONS = {
  // Party — two figures (people).
  party: (
    <HeaderIcon>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </HeaderIcon>
  ),
  // Access — a wheelchair.
  access: (
    <HeaderIcon>
      <circle cx="11" cy="4" r="2" />
      <path d="M11 6.5v4.5" />
      <path d="M11 11h3.5" />
      <path d="M11 11l2 3.5" />
      <circle cx="15" cy="17" r="4.5" />
    </HeaderIcon>
  ),
};

// Which party keys belong to the Access micro-group. Keys come from the
// party_options data; this only decides the group a known key sits in. Any key
// not listed here falls into the Party group so a new option still renders.
const ACCESS_KEYS = new Set(['accessible', 'big_rig']);

// Item 1 — padding unified with the When pill (TimeChips.timePill): px-3 py-1
// so Party and Access chips share the same height, horizontal padding, border
// radius (rounded-full) and font size (text-sm) as the When pills. The
// inline-flex items-center gap-1 layout is kept so the leading icon, label and
// active-state × still sit on one line; colours already matched timePill.
const partyChip = (active) =>
  `inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full border transition-colors whitespace-nowrap ${
    active
      ? 'border-[#32B1FF] bg-[#32B1FF] text-white'
      : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
  }`;

const PartyChips = ({ options, party, onChange, label = 'Party' }) => {
  const list = Array.isArray(options) ? options : [];
  if (list.length === 0) return null;

  const toggle = (opt) => {
    const next = { ...(party || {}) };
    if (next[opt.key] !== undefined) delete next[opt.key];
    else next[opt.key] = opt.value;
    onChange(next);
  };
  const partyGroup = list.filter((o) => !ACCESS_KEYS.has(o.key));
  const accessGroup = list.filter((o) => ACCESS_KEYS.has(o.key));

  const renderGroup = (groupKey, groupLabel, opts) => {
    if (!opts.length) return null;
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          {GROUP_ICONS[groupKey]}
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{groupLabel}</span>
        </div>
        <div className="flex items-center gap-1 flex-nowrap" role="group" aria-label={groupLabel}>
          {opts.map((opt) => {
            const active = party?.[opt.key] !== undefined;
            const Glyph = ICONS[opt.key];
            return (
              <button key={opt.key} type="button" aria-pressed={active} onClick={() => toggle(opt)} className={partyChip(active)}>
                {Glyph}
                {opt.label}
                {active && <XIcon />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1.5">
      {renderGroup('party', label, partyGroup)}
      {renderGroup('access', 'Access', accessGroup)}
    </div>
  );
};

export default PartyChips;
